import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Pencil, Search, Info, Trash2, Loader2, ChevronUpIcon, ChevronDownIcon } from 'lucide-react'
import toastService from '@/services/toast/service'
import {
  onboardingService,
  type AdminOnboardingCallRecord,
  type OnboardingCallListMeta,
} from '@/services/onboarding'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { generatePaginationPages } from '@/lib/pagination'
import { TableSkeleton } from '@/components/skeletons'
import { RelativeTime } from '@/components/RelativeTime'
import { useDebounce } from '@/hooks/useDebounce'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useCanEditAppointments, VIEWER_NO_PERMISSION } from '@/hooks/useIsViewer'
import EditCallModal from './EditCallModal'
import { CALL_STATUS_OPTIONS, CALL_STATUS_LABELS, CALL_STATUS_TONES, SOURCE_OPTIONS, SOURCE_LABELS, SOURCE_DESCRIPTIONS, resolveOutcomeDisplay, OUTCOME_LABELS, OUTCOME_TONES, OUTCOME_DESCRIPTIONS } from './onboarding-labels'

const ALL = 'all'

type SortColumn = 'startAtUtc' | 'createdAtUtc'
type SortState = { by: SortColumn; dir: 'asc' | 'desc' }

export default function OnboardingAppointments() {
  const readOnly = !useCanEditAppointments()
  const [rows, setRows] = useState<AdminOnboardingCallRecord[]>([])
  const [meta, setMeta] = useState<OnboardingCallListMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState(ALL)
  const [outcome, setOutcome] = useState('booked')
  const [source, setSource] = useState(ALL)
  const [device, setDevice] = useState(ALL)
  const [sort, setSort] = useState<SortState>({ by: 'startAtUtc', dir: 'desc' })
  const [editing, setEditing] = useState<AdminOnboardingCallRecord | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [removeOpen, setRemoveOpen] = useState(false)
  const [removing, setRemoving] = useState(false)

  const debouncedSearch = useDebounce(search, 300)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await onboardingService.listCalls({
        page,
        limit: 20,
        search: debouncedSearch || undefined,
        status: status === ALL ? undefined : status,
        outcome: outcome === ALL ? undefined : outcome,
        source: source === ALL ? undefined : source,
        device: device === ALL ? undefined : device,
        sortBy: sort.by,
        sortDir: sort.dir,
      })
      setRows(result.data)
      setSelectedIds(new Set())
      setMeta(result.meta)
    } catch {
      toastService.error("Couldn't load appointments")
    } finally {
      setLoading(false)
    }
  }, [page, debouncedSearch, status, outcome, source, device, sort])

  // A search or filter change goes back to page 1 before it fetches, so it
  // costs one request rather than one for the old page and one for the first.
  const filterKey = JSON.stringify([debouncedSearch, status, outcome, source, device, sort])
  const lastFilterKey = useRef(filterKey)
  useEffect(() => {
    if (lastFilterKey.current !== filterKey) {
      lastFilterKey.current = filterKey
      if (page !== 1) {
        setPage(1)
        return
      }
    }
    load()
  }, [load, filterKey, page])

  const handleSaved = (updated: AdminOnboardingCallRecord) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
    setEditing((prev) => (prev && prev.id === updated.id ? updated : prev))
  }

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id))

  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(rows.map((r) => r.id)))

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // Only rows on the current page can be ticked, and the selection is cleared
  // on every load, so every selected id is present in `rows`.
  const selectedRows = rows.filter((r) => selectedIds.has(r.id))

  const toggleSort = (column: SortColumn) =>
    setSort((prev) =>
      prev.by === column ? { by: column, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { by: column, dir: 'desc' },
    )

  const sortIcon = (column: SortColumn) => {
    if (sort.by !== column) {
      return <span className="inline-block ml-1 text-muted-foreground/40 text-xs">↕</span>
    }
    return sort.dir === 'asc' ? (
      <ChevronUpIcon className="h-3 w-3 inline ml-1" />
    ) : (
      <ChevronDownIcon className="h-3 w-3 inline ml-1" />
    )
  }

  const confirmRemove = async () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    setRemoving(true)
    try {
      const { queued } = await onboardingService.removeCalls(ids)
      toastService.success(
        `Added ${queued} appointment${queued === 1 ? '' : 's'} to the removal queue`,
      )
      setRemoveOpen(false)
      load()
    } catch {
      toastService.error("Couldn't queue the removal")
    } finally {
      setRemoving(false)
    }
  }

  const bookedFor = (row: AdminOnboardingCallRecord) =>
    row.startAtUtc
      ? new Date(row.startAtUtc).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZone: row.timezone ?? undefined,
        })
      : null

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Appointments</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Onboarding setup calls from new signups, mirrored with the GHL Onboarding calendar
            every minute.
          </p>
        </div>
        {selectedIds.size > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="w-full sm:w-auto">
                <Button
                  variant="destructive"
                  onClick={() => setRemoveOpen(true)}
                  disabled={loading || removing || readOnly}
                  className="w-full sm:w-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove ({selectedIds.size})
                </Button>
              </span>
            </TooltipTrigger>
            {readOnly && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
          </Tooltip>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        {/* Toolbar — search left, filters right */}
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between border-b">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search agency or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="h-9 w-[130px] text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All outcomes</SelectItem>
                <SelectItem value="booked">Booked</SelectItem>
                <SelectItem value="skipped">Skipped</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 w-[180px] text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {CALL_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="h-9 w-[150px] text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All sources</SelectItem>
                {SOURCE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={device} onValueChange={setDevice}>
              <SelectTrigger className="h-9 w-[140px] text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All devices</SelectItem>
                <SelectItem value="mobile">Mobile</SelectItem>
                <SelectItem value="desktop">Desktop</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <TableSkeleton columns={11} rows={8} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[40px] text-center">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleSelectAll}
                      disabled={readOnly || rows.length === 0}
                      aria-label="Select all appointments on this page"
                    />
                  </TableHead>
                  <TableHead className="min-w-[200px]">Customer</TableHead>
                  <TableHead className="text-center">Assigned</TableHead>
                  <TableHead className="text-center">Device</TableHead>
                  <TableHead className="text-center">Source</TableHead>
                  <TableHead className="text-center">Outcome</TableHead>
                  <TableHead
                    className="text-center cursor-pointer select-none"
                    onClick={() => toggleSort('startAtUtc')}
                  >
                    Booked for {sortIcon('startAtUtc')}
                  </TableHead>
                  <TableHead
                    className="text-center cursor-pointer select-none"
                    onClick={() => toggleSort('createdAtUtc')}
                  >
                    Created {sortIcon('createdAtUtc')}
                  </TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Notes</TableHead>
                  <TableHead className="text-center w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="h-24 text-center text-muted-foreground">
                      No appointments yet
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} className="hover:bg-muted/50 transition-colors">
                    <TableCell className="text-center">
                      <Checkbox
                        checked={selectedIds.has(row.id)}
                        onCheckedChange={() => toggleSelectOne(row.id)}
                        disabled={readOnly}
                        aria-label={`Select ${row.contact?.name ?? row.agency?.name ?? row.contact?.email ?? 'appointment'}`}
                      />
                    </TableCell>
                    <TableCell>
                      {row.agency ? (
                        <Link to={`/agencies/${row.agency.id}`} className="font-medium hover:underline">
                          {row.contact?.name ?? row.agency.name}
                        </Link>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="font-medium">{row.contact?.name ?? '—'}</span>
                          <Badge tone="warning">No account</Badge>
                        </span>
                      )}
                      <div className="text-xs text-muted-foreground">{row.contact?.email ?? ''}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.planAtDecision || '—'}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">{row.assignedUserName ?? <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-center">
                      {row.deviceBrand || row.browser ? (
                        <>
                          <div className="text-sm">{[row.deviceBrand, row.browser].filter(Boolean).join(' · ')}</div>
                          {row.device && <div className="text-xs text-muted-foreground capitalize">{row.device}</div>}
                        </>
                      ) : row.device ? (
                        <span className="capitalize">{row.device}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center justify-center gap-1.5 text-sm">
                        {SOURCE_LABELS[row.source] ?? row.source}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-flex cursor-default">
                              <Info className="h-3.5 w-3.5 text-muted-foreground" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[320px]">
                            <p>{SOURCE_DESCRIPTIONS[row.source] ?? row.source}</p>
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        const od = resolveOutcomeDisplay(row.outcome, row.source)
                        return (
                          <span className="inline-flex items-center justify-center gap-1.5">
                            <Badge tone={OUTCOME_TONES[od]}>{OUTCOME_LABELS[od]}</Badge>
                            {od === 'declined' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span tabIndex={0} className="inline-flex cursor-default">
                                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[280px]">
                                  <p>{OUTCOME_DESCRIPTIONS[od]}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </span>
                        )
                      })()}
                    </TableCell>
                    <TableCell className="text-center whitespace-nowrap">
                      {bookedFor(row) ? (
                        <>
                          <div>{bookedFor(row)}</div>
                          <div className="text-xs text-muted-foreground">{row.timezone}</div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center whitespace-nowrap">
                      <RelativeTime date={row.createdAtUtc} />
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        {row.outcome === 'booked' ? (
                          <Badge tone={CALL_STATUS_TONES[row.status] ?? 'neutral'}>
                            {CALL_STATUS_LABELS[row.status] ?? row.status}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                        {row.removalError && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0} className="inline-flex cursor-default">
                                <Badge tone="danger">Removal failed</Badge>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[320px]">
                              <p>{row.removalError}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {row.notes.length > 0 ? (
                        <div className="max-w-[280px] mx-auto">
                          <span className="block whitespace-normal break-words text-sm text-muted-foreground">
                            {row.notes[0].body}
                          </span>
                          {row.notes.length > 1 && (
                            <span className="text-xs text-muted-foreground">
                              +{row.notes.length - 1} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center">
                        <div className="inline-flex items-center rounded-md border">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={readOnly}
                                  className="h-8 w-8 rounded-md"
                                  onClick={() => setEditing(row)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{readOnly ? VIEWER_NO_PERMISSION : 'Edit appointment'}</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination footer */}
        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-4 border-t">
            <span className="text-sm text-muted-foreground">
              Showing {(meta.page - 1) * meta.limit + 1}–{Math.min(meta.page * meta.limit, meta.total)} of {meta.total}
            </span>
            <Pagination className="mx-0 w-auto">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={meta.page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                {generatePaginationPages(meta.page, meta.totalPages).map((p, idx) => (
                  <PaginationItem key={`${p}-${idx}`}>
                    {p === 'ellipsis' ? (
                      <span className="px-2 text-muted-foreground">…</span>
                    ) : (
                      <PaginationLink
                        isActive={p === meta.page}
                        onClick={() => setPage(p as number)}
                        className="cursor-pointer"
                      >
                        {p}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                    className={meta.page >= meta.totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>

      {editing && (
        <EditCallModal
          key={editing.id}
          call={editing}
          open={!!editing}
          onOpenChange={(open) => !open && setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      <Dialog
        open={removeOpen}
        onOpenChange={(open) => !open && !removing && setRemoveOpen(false)}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              Remove {selectedIds.size} appointment{selectedIds.size === 1 ? '' : 's'}
            </DialogTitle>
            <DialogDescription>
              Added to the removal queue and deleted from the GHL Onboarding calendar in the
              background within a few minutes.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            {/* Exactly what is about to go, so a mis-tick is caught here. */}
            <div className="max-h-52 divide-y overflow-y-auto rounded-md border bg-muted/30">
              {selectedRows.map((row) => (
                <div key={row.id} className="truncate px-3 py-2 text-sm">
                  {row.contact?.email ?? 'No email'}
                </div>
              ))}
            </div>

            <div className="bg-destructive/10 border-l-4 border-destructive p-3 text-destructive rounded-r text-sm">
              <strong>Warning:</strong> This action is irreversible. Anyone with an account will
              be offered the setup call again. If a deletion fails, the appointment comes back
              with a Removal failed badge so you can retry.
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoveOpen(false)} disabled={removing}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmRemove} disabled={removing}>
              {removing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {removing
                ? 'Removing...'
                : `Remove ${selectedIds.size} appointment${selectedIds.size === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
